import { useEffect, useState } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import { Failed, PaneSkeleton, useDiscountWrite } from "./DiscountParts";
import {
  ALL, baseHint, BASIS_WORD, deleteRate, DISCOUNTS_COPY as T, funnelNotes, funnelStale, FUNNEL_WORD, groupEmptyNote, groupFunnelLine, MARKETS,
  netLane, netPricesQuery, netRowsByProgramme, NO_FUNNEL, noRateSet, partnerGroups, partnersQuery, productKey, productOptions, programmeBody,
  programmeDone, programmeDraft, putProgramme, putRate, rateBody, rateDone, rateDraft, rateLabel, rateMarketWord, rateRemovedDone, rateUnchanged,
  refreshPartners, retireProgramme, variantWord, type Basis, type NetPrices, type NetRow, type PartnerGroup, type Partners, type ProductOption,
  type Programme, type ProgrammeDraft, type RateBody, type RateDraft, type RateKey, type RateRow,
} from "./discounts";
import { messageFor, money, personName, stamp, vatPct } from "./format";
import { NOTE_MAX } from "./strategies";
import { FormError, ResultLine, SectionHead, type Editor, type Result } from "./StrategyParts";

const P = T.partners;
const NAME_MAX = 120;

/**
 * The Distribution Partners pane (C1 v1.22, item5): Add programme, then one card per programme with its terms, Edit programme and
 * Retire, its product rates as rows to change in place with an Add row, and the net price it leaves per SKU. v26 designs no editor
 * here, so the rows follow the Config tab's (a box per value, Save and Remove on the row) and the forms the Strategies tab's. New
 * programmes come off the Shopify price (owner ruling 2026-09-27). Dialogs mount in an anchor inside the pane, so they stay inside the gate.
 * C1 v1.48 (c1-funnel-partners, G3): the cards sit under the Distribution Funnel's partner types, each type saying what the funnel
 * lists for it (counts per lead stage); a type with points and no programme reads new in the funnel; the stale banner and the sync
 * date come off the Partners read's `funnel` block.
 */
export function PartnersPane({ areaName }: { areaName: string }) {
  const partners = useQuery(partnersQuery());
  const nets = useQueries({ queries: MARKETS.map((m) => netPricesQuery(m)) });
  const [result, setResult] = useState<Result>(null);
  const [creating, setCreating] = useState(false);
  // State rather than a ref (the StrategiesTab pattern): a dialog's portal container has to be a rendered element when it mounts.
  const [anchor, setAnchor] = useState<HTMLDivElement | null>(null);
  const editor: Editor = { areaName, report: setResult, anchor };
  const head = (withAdd: boolean) => (
    <SectionHead title={P.title} desc={P.desc}>{withAdd && <Button size="sm" onClick={() => setCreating(true)}>{P.add}</Button>}</SectionHead>
  );
  const all = [partners, ...nets];
  const missing = all.find((q) => q.data === undefined && q.error)?.error;
  const error = all.find((q) => q.error)?.error;
  const retry = () => { for (const q of all) void q.refetch(); };
  if (all.some((q) => q.isPending) && !missing) return <section className="flex flex-col gap-2">{head(false)}<PaneSkeleton /></section>;
  if (missing || !partners.data) return <section className="flex flex-col gap-2">{head(false)}<Failed sentence={`${P.cannotLoad} ${messageFor(missing ?? error, areaName)}`} onRetry={retry} /></section>;
  const data = partners.data;
  const rows = netRowsByProgramme(nets.map((q) => q.data).filter((d): d is NetPrices => d !== undefined));
  const stale = funnelStale(data.funnel);
  return (
    <section className="flex flex-col gap-3">
      {head(true)}
      {error && <Failed sentence={P.notRefreshed} onRetry={retry} />}
      {stale && <Alert role="note" data-funnel-stale=""><AlertDescription>{stale}</AlertDescription></Alert>}
      <ResultLine result={result} />
      {creating && <ProgrammeForm programme={null} partners={data} editor={editor} onClose={() => setCreating(false)} />}
      {data.programmes.length === 0 && <Card><CardContent className="py-6"><p className="font-medium text-ks-ink">{P.empty}</p></CardContent></Card>}
      {partnerGroups(data).map((g) => <TypeGroup key={g.type ?? "none"} group={g} rows={rows} partners={data} editor={editor} />)}
      {funnelNotes(data.funnel).map((line) => <p key={line} className="text-sm text-ks-grey-1" data-funnel-note="">{line}</p>)}
      {data.programmes.length > 0 && <p className="text-sm text-ks-grey-1">{P.note}</p>}
      <div ref={setAnchor} data-dialog-anchor="" />
    </section>
  );
}

/** One partner type: its name and what the Distribution Funnel lists for it, then its programmes' cards, or the note that it has
 * none (new in the funnel when the funnel lists points of it). The no-type group says how to place its programmes. */
function TypeGroup({ group, rows, partners, editor }: { group: PartnerGroup; rows: Record<number, Array<NetRow & { market: string }>>; partners: Partners; editor: Editor }) {
  const G = P.group;
  const line = groupFunnelLine(group);
  const empty = groupEmptyNote(group);
  return (
    <div className="flex flex-col gap-2" data-partner-type={group.type ?? "none"}>
      <div className="flex flex-wrap items-baseline gap-2">
        <h4 className="text-base font-semibold text-ks-ink">{group.type === null ? G.noType : FUNNEL_WORD[group.type] ?? group.type}</h4>
        {line && <span className="text-sm text-ks-grey-1">{line}</span>}
      </div>
      {group.type === null && <p className="text-sm text-ks-grey-1">{G.noTypeHint}</p>}
      {empty && <p className="text-sm text-ks-grey-1" data-type-note="">{empty}</p>}
      {group.programmes.map((p) => <ProgrammeCard key={p.id} programme={p} rows={rows[p.id] ?? []} partners={partners} editor={editor} />)}
    </div>
  );
}

/** Add or edit a programme: name (fixed once it exists: the store keys a programme by name), default rate, per unit or per case, the
 * funnel type, the volume note and the note. The base is shown, not chosen (owner ruling 2026-09-27). */
export function ProgrammeForm({ programme, partners, editor, onClose }: { programme: Programme | null; partners: Partners; editor: Editor; onClose: () => void }) {
  const [draft, setDraft] = useState<ProgrammeDraft>(programmeDraft(programme));
  const [error, setError] = useState<string | null>(null);
  const write = useDiscountWrite(editor, putProgramme, (answer) => programmeDone(programme, answer), refreshPartners);
  const F = P.form;
  const set = (patch: Partial<ProgrammeDraft>) => setDraft({ ...draft, ...patch });
  const submit = () => {
    const checked = programmeBody(draft, programme, partners.programmes);
    if (!checked.ok) { setError(checked.error); return; }
    setError(null);
    write.mutate(checked.value, { onSuccess: onClose });
  };
  const id = (field: string) => `pp-programme-${programme?.id ?? "new"}-${field}`;
  return (
    <Card><CardContent className="flex flex-col gap-3 py-4">
      <span className="font-semibold text-ks-ink">{programme ? F.editTitle(programme.name) : F.createTitle}</span>
      <div className="flex flex-wrap items-end gap-3">
        {!programme && (
          <div className="flex flex-col gap-1"><Label htmlFor={id("name")} className="text-xs">{F.name}</Label>
            <Input id={id("name")} className="w-64" maxLength={NAME_MAX} value={draft.name} onChange={(e) => set({ name: e.target.value })} /></div>
        )}
        <div className="flex flex-col gap-1"><Label htmlFor={id("rate")} className="text-xs">{F.defaultRate}</Label>
          <div className="flex items-center gap-1"><Input id={id("rate")} inputMode="decimal" className="w-20" value={draft.default_rate} onChange={(e) => set({ default_rate: e.target.value })} /><span className="text-xs text-ks-grey-1">{T.editor.pctOff}</span></div></div>
        <div className="flex flex-col gap-1"><Label className="text-xs">{F.basis}</Label>
          <Select value={draft.basis} onValueChange={(v) => set({ basis: v as Basis })}>
            <SelectTrigger className="w-40" aria-label={F.basis}><SelectValue placeholder={F.pickBasis} /></SelectTrigger>
            <SelectContent>{(["unit", "case"] as Basis[]).map((b) => <SelectItem key={b} value={b}>{BASIS_WORD[b]}</SelectItem>)}</SelectContent>
          </Select></div>
        <div className="flex flex-col gap-1"><Label className="text-xs">{F.funnel}</Label>
          <Select value={draft.funnel_type} onValueChange={(v) => set({ funnel_type: v })}>
            <SelectTrigger className="w-40" aria-label={F.funnel}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_FUNNEL}>{F.noFunnel}</SelectItem>
              {partners.vocabulary.funnel_type.map((f) => <SelectItem key={f} value={f}>{FUNNEL_WORD[f] ?? f}</SelectItem>)}
            </SelectContent>
          </Select></div>
      </div>
      <p className="text-xs text-ks-grey-1">{F.defaultHint} {F.base(programme ? programme.base : "shopify")}</p>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1"><Label htmlFor={id("volume")} className="text-xs">{F.volumeNote} ({F.optional})</Label>
          <Input id={id("volume")} className="w-80" maxLength={NOTE_MAX} value={draft.volume_note} onChange={(e) => set({ volume_note: e.target.value })} /></div>
        <div className="flex flex-col gap-1"><Label htmlFor={id("note")} className="text-xs">{F.note} ({F.optional})</Label>
          <Input id={id("note")} className="w-80" maxLength={NOTE_MAX} value={draft.note} onChange={(e) => set({ note: e.target.value })} /></div>
      </div>
      <FormError error={error} />
      <div className="flex gap-2">
        <Button size="sm" onClick={submit} disabled={write.isPending}>{programme ? F.save : F.create}</Button>
        <Button size="sm" variant="outline" data-nonmutating onClick={onClose}>{F.cancel}</Button>
      </div>
    </CardContent></Card>
  );
}

/** One programme: its terms line and who set it, Edit programme and Retire, then its product rates and the net price per SKU. */
function ProgrammeCard({ programme, rows, partners, editor }: { programme: Programme; rows: Array<NetRow & { market: string }>; partners: Partners; editor: Editor }) {
  const [editing, setEditing] = useState(false);
  const [retiring, setRetiring] = useState(false);
  const retire = useDiscountWrite(editor, retireProgramme, (answer) => P.done.retired(answer.name, answer.rates_retired), refreshPartners, () => setRetiring(false));
  const options = productOptions(rows);
  return (
    <Card><CardContent className="flex flex-col gap-3 py-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-ks-ink">{programme.name}</span>
        <Badge variant="secondary">{P.terms(programme.basis, programme.base, programme.default_rate === null ? null : vatPct(programme.default_rate))}</Badge>
        {programme.rate_count > 0 && <Badge variant="outline">{P.rates(programme.rate_count)}</Badge>}
        {programme.funnel_type && <span className="text-xs text-ks-grey-1">{P.funnel(programme.funnel_type)}</span>}
        <span className="text-xs text-ks-grey-1">{P.setBy(personName(programme.set_by), stamp(programme.set_at), programme.source)}</span>
        <span className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setEditing(true)} disabled={editing}>{P.edit}</Button>
          <Button size="sm" variant="outline" onClick={() => setRetiring(true)}>{P.retire}</Button>
        </span>
      </div>
      {programme.volume_note && <p className="text-sm text-ks-grey-1">{programme.volume_note}</p>}
      {programme.note && <p className="text-sm text-ks-grey-1">{programme.note}</p>}
      {editing && <ProgrammeForm programme={programme} partners={partners} editor={editor} onClose={() => setEditing(false)} />}
      <span className="text-sm font-semibold text-ks-ink">{P.ratesTitle}</span>
      <Table>
        <TableHeader><TableRow>{P.rateColumns.map((c, i) => <TableHead key={i}>{c}</TableHead>)}</TableRow></TableHeader>
        <TableBody>
          {programme.rates.length === 0 && <TableRow><TableCell colSpan={5} className="text-ks-grey-1">{P.noRates(programme.default_rate === null ? null : vatPct(programme.default_rate))}</TableCell></TableRow>}
          {programme.rates.map((r) => <RateRowEditor key={r.id} programme={programme} row={r} editor={editor} />)}
          <NewRateRow programme={programme} options={options} editor={editor} />
        </TableBody>
      </Table>
      <span className="text-sm font-semibold text-ks-ink">{P.netTitle}</span>
      <Table>
        <TableHeader><TableRow>{P.columns.map((c) => <TableHead key={c}>{c}</TableHead>)}</TableRow></TableHeader>
        <TableBody>
          {rows.length === 0 && <TableRow><TableCell colSpan={4} className="text-ks-grey-1">{P.noRows}</TableCell></TableRow>}
          {rows.map((r) => <NetLine key={`${r.market}|${r.xero_sku}|${r.marketplace_id}`} row={r} />)}
        </TableBody>
      </Table>
      {retiring && editor.anchor && (
        <RetireDialog programme={programme} container={editor.anchor} pending={retire.isPending} onConfirm={() => retire.mutate(programme.id)} onClose={() => setRetiring(false)} />
      )}
    </CardContent></Card>
  );
}

/** A product rate in place: its rate box (empty and disabled while Not available is ticked), the tick, the note, Save and Remove. */
function RateRowEditor({ programme, row, editor }: { programme: Programme; row: RateRow; editor: Editor }) {
  const [draft, setDraft] = useState<RateDraft>(rateDraft(row));
  const [error, setError] = useState<string | null>(null);
  // The read is the truth: a save elsewhere re-versions the row under a new id (and a new key), so only a changed value lands here.
  useEffect(() => { setDraft(rateDraft(row)); }, [row.id, row.rate, row.available, row.note]);
  const save = useDiscountWrite(editor, (body: RateBody) => putRate(programme.id, body), (answer, body) => rateDone(programme, body, answer), refreshPartners);
  const remove = useDiscountWrite(editor, (key: RateKey) => deleteRate(programme.id, key), (_answer, key) => rateRemovedDone(programme, key), refreshPartners);
  const product = rateLabel(row);
  const market = rateMarketWord(row.market);
  const submit = () => {
    const checked = rateBody(draft, programme, false);
    if (!checked.ok) { setError(checked.error); return; }
    setError(null);
    save.mutate(checked.value);
  };
  const pending = save.isPending || remove.isPending;
  return (
    <TableRow>
      <TableCell className="whitespace-normal"><span className="font-medium text-ks-ink">{product}</span><div className="text-xs text-ks-grey-1">{P.setBy(personName(row.set_by), stamp(row.set_at), row.source)}</div></TableCell>
      <TableCell>{market}</TableCell>
      <TableCell>
        <RateBoxes draft={draft} setDraft={setDraft} label={P.rate.label(programme.name, product, market)} availableLabel={P.rate.availableLabel(programme.name, product, market)} />
        <FormError error={error} />
      </TableCell>
      <TableCell><Input className="h-7 w-full min-w-40 text-xs" maxLength={NOTE_MAX} aria-label={P.rate.noteLabel(programme.name, product, market)} placeholder={T.editor.notePlaceholder} value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} /></TableCell>
      <TableCell className="text-right">
        <Button size="sm" onClick={submit} disabled={pending || rateUnchanged(row, draft)}>{P.rate.save}</Button>
        <Button size="sm" variant="outline" className="ml-2" onClick={() => remove.mutate({ tracking: row.tracking, variant: row.variant, market: row.market })} disabled={pending}>{P.rate.remove}</Button>
      </TableCell>
    </TableRow>
  );
}
/** The Add row: a product the programme can price (from its net rows), All variants or one, All markets or one, then the rate. */
function NewRateRow({ programme, options, editor }: { programme: Programme; options: ProductOption[]; editor: Editor }) {
  const [draft, setDraft] = useState<RateDraft>(rateDraft(null));
  const [error, setError] = useState<string | null>(null);
  const add = useDiscountWrite(editor, (body: RateBody) => putRate(programme.id, body), (answer, body) => rateDone(programme, body, answer), refreshPartners);
  const R = P.rate;
  const variants = options.find((o) => o.tracking === draft.tracking)?.variants ?? [];
  const submit = () => {
    const checked = rateBody(draft, programme, true);
    if (!checked.ok) { setError(checked.error); return; }
    setError(null);
    add.mutate(checked.value, { onSuccess: () => setDraft(rateDraft(null)) });
  };
  const subject = draft.tracking ? rateLabel(draft) : R.pickProduct;
  return (
    <TableRow>
      <TableCell>
        <div className="flex flex-col gap-1">
          <Select value={draft.tracking} onValueChange={(v) => setDraft({ ...draft, tracking: v, variant: ALL })}>
            <SelectTrigger className="w-56" aria-label={`${R.product}, ${programme.name}`}><SelectValue placeholder={R.pickProduct} /></SelectTrigger>
            <SelectContent>{options.map((o) => <SelectItem key={o.tracking} value={o.tracking}>{o.tracking}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={draft.variant} onValueChange={(v) => setDraft({ ...draft, variant: v })} disabled={!draft.tracking}>
            <SelectTrigger className="w-56" aria-label={`${R.variant}, ${programme.name}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{variantWord(ALL)}</SelectItem>
              {variants.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </TableCell>
      <TableCell>
        <Select value={draft.market} onValueChange={(v) => setDraft({ ...draft, market: v })}>
          <SelectTrigger className="w-32" aria-label={`${R.market}, ${programme.name}`}><SelectValue /></SelectTrigger>
          <SelectContent>{[ALL, ...MARKETS].map((m) => <SelectItem key={m} value={m}>{rateMarketWord(m)}</SelectItem>)}</SelectContent>
        </Select>
      </TableCell>
      <TableCell>
        <RateBoxes draft={draft} setDraft={setDraft} label={R.label(programme.name, subject, rateMarketWord(draft.market))} availableLabel={R.availableLabel(programme.name, subject, rateMarketWord(draft.market))} />
        <FormError error={error} />
      </TableCell>
      <TableCell><Input className="h-7 w-full min-w-40 text-xs" maxLength={NOTE_MAX} aria-label={R.noteLabel(programme.name, subject, rateMarketWord(draft.market))} placeholder={T.editor.notePlaceholder} value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} /></TableCell>
      <TableCell className="text-right"><Button size="sm" onClick={submit} disabled={add.isPending}>{R.add}</Button></TableCell>
    </TableRow>
  );
}

function RateBoxes({ draft, setDraft, label, availableLabel }: { draft: RateDraft; setDraft: (d: RateDraft) => void; label: string; availableLabel: string }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Input inputMode="decimal" className="h-7 w-16" aria-label={label} value={draft.available ? draft.rate : ""} disabled={!draft.available} onChange={(e) => setDraft({ ...draft, rate: e.target.value })} />
      <span className="text-xs text-ks-grey-1">{T.editor.pctOff}</span>
      <label className="flex items-center gap-1 text-xs text-ks-grey-1">
        <Checkbox aria-label={availableLabel} checked={!draft.available} onCheckedChange={(v) => setDraft({ ...draft, available: v !== true })} />{P.rate.notAvailable}
      </label>
    </div>
  );
}

/** v26-style confirm before a programme and all its rates retire. Mounted in the pane's in-gate anchor. */
function RetireDialog({ programme, container, pending, onConfirm, onClose }: { programme: Programme; container: HTMLElement; pending: boolean; onConfirm: () => void; onClose: () => void }) {
  const D = P.retireDialog;
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent container={container} className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>{D.title(programme.name)}</DialogTitle>
          <DialogDescription className="text-ks-ink">{D.body(programme.rate_count)}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" data-nonmutating onClick={onClose}>{D.cancel}</Button>
          <Button variant="destructive" disabled={pending} onClick={onConfirm}>{D.confirm}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NetLine({ row }: { row: NetRow & { market: string } }) {
  const hint = baseHint(row, money);
  return (
    <TableRow>
      <TableCell className="font-medium text-ks-ink">{productKey(row)}<div className="text-xs text-ks-grey-1">{row.xero_sku}</div></TableCell>
      <TableCell>{netLane(row)}</TableCell>
      <TableCell>
        {noRateSet(row)
          ? <Badge variant="secondary">{P.noRateSet}</Badge>
          : row.rate === null || !row.available
            ? <span className="text-ks-grey-1">{row.available ? T.subscriptions.none : P.notAvailable}</span>
            : <><Badge variant="outline">{T.subscriptions.off(vatPct(row.rate))}</Badge><div className="text-xs text-ks-grey-1">{row.resolved_from === "programme" ? P.fromDefault : P.fromRate}</div></>}
      </TableCell>
      <TableCell className="tabular-nums">
        {row.net_price !== null
          ? <><span className="font-medium text-ks-ink">{money(row.net_price, row.currency)}</span>{hint && <div className="text-xs text-ks-grey-1">{hint}</div>}</>
          : <span className="text-sm text-ks-grey-1">{noRateSet(row) ? P.noRateSet : row.not_priced}</span>}
      </TableCell>
    </TableRow>
  );
}
